// "Sign in with Google" integration for the admin back-office, using Google
// Identity Services (GIS). We use the "auth code" popup flow: GIS returns a
// one-time authorization code that the server exchanges (redirect_uri
// "postmessage") for an id_token — mirroring the Apple authorization-code flow.
//
// The popup is opened by GIS itself from `requestCode()`, so it should be
// called from a user gesture (the button's onClick).

const GIS_SDK_URL = "https://accounts.google.com/gsi/client";

// OAuth 2.0 Web client ID (Google Cloud Console). Set at build time.
const GOOGLE_WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

let sdkPromise = null;
let codeClient = null;
// The GIS code client takes a single callback at creation time, so we relay the
// result to the in-flight signInWithGoogle() promise via this deferred.
let pending = null;

const loadSdk = () => {
	if (typeof window !== "undefined" && window.google?.accounts?.oauth2) {
		return Promise.resolve();
	}
	if (sdkPromise) {
		return sdkPromise;
	}
	sdkPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = GIS_SDK_URL;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () =>
			reject(new Error("Failed to load the Google Identity Services SDK"));
		document.head.appendChild(script);
	});
	return sdkPromise;
};

// Preload the SDK as soon as this module is imported so that, by the time the
// user clicks, requestCode() can open the popup without losing the gesture.
if (typeof window !== "undefined") {
	loadSdk().catch(() => {});
}

const ensureClient = async () => {
	await loadSdk();
	if (!codeClient) {
		codeClient = window.google.accounts.oauth2.initCodeClient({
			client_id: GOOGLE_WEB_CLIENT_ID,
			scope: "openid email profile",
			ux_mode: "popup",
			callback: response => {
				if (!pending) {
					return;
				}
				const { resolve, reject } = pending;
				pending = null;
				if (response && response.code) {
					resolve({ authorizationCode: response.code });
				} else {
					reject(response || new Error("Google sign-in failed"));
				}
			},
			error_callback: err => {
				if (!pending) {
					return;
				}
				const { reject } = pending;
				pending = null;
				reject(err);
			}
		});
	}
};

// Opens the Google popup and resolves with { authorizationCode } for the server.
export const signInWithGoogle = async () => {
	await ensureClient();
	return new Promise((resolve, reject) => {
		pending = { resolve, reject };
		codeClient.requestCode();
	});
};

// GIS reports a user-cancelled / blocked popup via these markers. Lets callers
// ignore a cancel rather than surfacing a real error.
export const isGoogleCancel = error =>
	Boolean(
		error &&
			(error.type === "popup_closed" ||
				error.type === "popup_failed_to_open" ||
				error.error === "popup_closed_by_user" ||
				error.error === "access_denied")
	);
